import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Globe, RefreshCw, RefreshCwOff, DownloadCloud, Info } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
  StatusBadge,
  ActionsMenuCell,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import {
  adminDomainService,
  type AdminDomainRow,
} from '@/domains/domains-registry/services/adminDomainService';
import { DomainsBalanceBanner } from '../../components/DomainsBalanceBanner';
import { TldMarginCard } from './TldMarginCard';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { formatDate } from '@/shared/utils/format';

type StatusFilter = 'all' | 'expiring' | 'expired' | 'active' | 'unhydrated';
type AutoRenewFilter = 'all' | 'off' | 'on';

const FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'expiring', label: 'Expiring < 30d' },
  { value: 'expired', label: 'Expired' },
  { value: 'active', label: 'Active' },
  { value: 'unhydrated', label: 'Not yet refreshed' },
];

const AUTO_RENEW_OPTIONS: { value: AutoRenewFilter; label: string }[] = [
  { value: 'all', label: 'Any auto-renew' },
  { value: 'off', label: 'Auto-renew off' },
  { value: 'on', label: 'Auto-renew on' },
];

/**
 * Query params arrive from the Domains coverage report's tiles. They were cast
 * straight to the filter type, so `?status=typo` silently selected a value no
 * row can match and the list read as empty rather than as misaddressed.
 */
function parseParam<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

function statusOf(days: number | null): 'expired' | 'expiring' | 'active' | 'unknown' {
  if (days === null) return 'unknown';
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'active';
}

const STATUS_TONE: Record<'expired' | 'expiring' | 'active' | 'unknown', StatusTone> = {
  expiring: 'expiring',
  expired: 'overdue',
  active: 'active',
  unknown: 'draft',
};

const STATUS_LABEL: Record<'expired' | 'expiring' | 'active' | 'unknown', string> = {
  expiring: 'Expiring',
  expired: 'Expired',
  active: 'Active',
  unknown: 'Unknown',
};

export default function AdminDomainsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const presetFilter = parseParam(
    searchParams.get('status'),
    FILTER_OPTIONS.map((o) => o.value),
    'all',
  );
  const presetAutoRenew = parseParam(
    searchParams.get('autorenew'),
    AUTO_RENEW_OPTIONS.map((o) => o.value),
    'all',
  );

  const [rows, setRows] = useState<AdminDomainRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(presetFilter);
  const [autoRenewFilter, setAutoRenewFilter] = useState<AutoRenewFilter>(presetAutoRenew);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  function reload(): Promise<AdminDomainRow[]> {
    setLoading(true);
    setError(null);
    return adminDomainService
      .listAll()
      .then((data) => {
        setRows(data);
        setLoading(false);
        return data;
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
        return [];
      });
  }

  async function onRefreshFromRegistrar(showToast = true) {
    setRefreshing(true);
    try {
      const r = await adminDomainService.bulkRefresh();
      if (showToast) {
        toast({
          tone: 'success',
          title: 'Refreshed from registrar',
          body: `${r.updated_count} domain${r.updated_count === 1 ? '' : 's'} updated · ${r.missing_count} missing at registrar`,
        });
      }
      await reload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Refresh failed',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRefreshing(false);
    }
  }

  async function onImport() {
    setImporting(true);
    try {
      const r = await adminDomainService.importFromRegistrar({ dryRun: false });
      toast({
        tone: 'success',
        title: 'Import complete',
        body: `${r.newly_linked} new · ${r.already_linked} already linked · ${r.unmatched_count} unmatched`,
      });
      await reload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Import failed',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setImporting(false);
    }
  }

  async function actOn(rowId: string, action: () => Promise<unknown>, label: string) {
    setBusyRowId(rowId);
    try {
      const r = (await action()) as { message?: string } | undefined;
      toast({ tone: 'success', title: label, body: r?.message });
      await reload();
    } catch (err) {
      toast({
        tone: 'danger',
        title: `${label} failed`,
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyRowId(null);
    }
  }

  // Initial load — and auto-hydrate if any row has no expiry data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await reload();
      if (cancelled || data.length === 0) return;
      const anyMissing = data.some((d) => d.expiry_date === null);
      if (anyMissing) {
        // Silent auto-hydrate — no toast unless it fails.
        await onRefreshFromRegistrar(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deliberate: this effect is meant to run on the listed deps only. (Was an
    // eslint-disable for react-hooks/exhaustive-deps; this config does not
    // register that plugin, so the directive was dangling.)
  }, []);

  /**
   * A domain list is STOCK, so the default period is `all-time` (Rule T-1), and
   * the date filtered is `Expires` — not "created". The question staff actually
   * ask of this page is "what runs out in the next N", and §4.2 of the plan
   * classified it that way.
   *
   * CAVEAT WORTH KNOWING, because it is visible in the count. `expiry_date` is
   * NULL until bulk-refresh hydrates a row from the registrar, and `inPeriod()`
   * treats a null event date as "not in range". So selecting any period drops
   * unhydrated domains out of the list. That is the honest answer — we do not
   * know when they expire, so we cannot claim they expire inside your window —
   * but it means `N of M` will shrink by the unhydrated count the moment a
   * period is chosen. The "Not yet hydrated" status filter still finds them,
   * and the banner above already reports how many there are.
   */
  const table = useTableFilters<AdminDomainRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Expires',
    dateOf: (r) => r.expiry_date,
    searchText: (r) => [r.domain_name, r.customer_name, r.customer_email ?? ''].join(' '),
    facets: useMemo(
      () => [
        (r: AdminDomainRow) =>
          statusFilter === 'unhydrated'
            ? r.expiry_date === null
            : statusFilter === 'all' || statusOf(r.days_to_expiry) === statusFilter,
        (r: AdminDomainRow) =>
          autoRenewFilter === 'all' ||
          (autoRenewFilter === 'on' ? r.auto_renew : !r.auto_renew),
      ],
      [statusFilter, autoRenewFilter],
    ),
  });

  const filtered = table.rows;

  const unhydratedCount = useMemo(
    () => rows.filter((r) => r.expiry_date === null).length,
    [rows],
  );

  const columns = useMemo<UniversalTableColumn<AdminDomainRow>[]>(
    () => [
      {
        key: 'domain',
        header: 'Domain',
        sortValue: (r) => r.domain_name,
        render: (r) => (
          <span className="inline-flex items-center gap-2 text-sm tabular-nums text-foreground">
            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
            {r.domain_name}
          </span>
        ),
      },
      {
        key: 'owner',
        header: 'Owner',
        sortValue: (r) => r.customer_name,
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.customer_name}</span>
            {r.customer_email && (
              <span className="text-xs text-muted-foreground">{r.customer_email}</span>
            )}
          </div>
        ),
      },
      {
        key: 'expiry',
        header: 'Expiry',
        sortValue: (r) => (r.expiry_date ? new Date(r.expiry_date).getTime() : Infinity),
        render: (r) => {
          if (!r.expiry_date) {
            return <span className="text-xs italic text-muted-foreground">not refreshed</span>;
          }
          return (
            <div className="flex flex-col">
              <span className="text-foreground">{formatDate(r.expiry_date)}</span>
              {r.days_to_expiry !== null && (
                <span className="text-xs text-muted-foreground">
                  {r.days_to_expiry < 0
                    ? `${Math.abs(r.days_to_expiry)}d overdue`
                    : `in ${r.days_to_expiry}d`}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => statusOf(r.days_to_expiry),
        render: (r) => {
          const s = statusOf(r.days_to_expiry);
          return <StatusBadge tone={STATUS_TONE[s]}>{STATUS_LABEL[s]}</StatusBadge>;
        },
        width: 'w-32',
      },
      {
        // The one fact that decides whether an expiry date is a diary note or a
        // deadline, and the list had no way to show it until `auto_renew`
        // reached the view (2026-08-31). "Off" is the state that needs action,
        // so it is the one that carries colour.
        key: 'autorenew',
        header: 'Auto-renew',
        sortValue: (r) => (r.auto_renew ? 1 : 0),
        render: (r) =>
          r.auto_renew ? (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              On
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-warning-dark">
              <RefreshCwOff className="h-3.5 w-3.5" aria-hidden />
              Off
            </span>
          ),
        width: 'w-32',
        hideOnMobile: true,
      },
      {
        key: 'actions',
        header: '',
        render: (r) => (
          <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
            <ActionsMenuCell
              ariaLabel={`Actions for ${r.domain_name}`}
              items={[
                {
                  label: 'Open detail',
                  onSelect: () => navigate(`/admin/domains/${encodeURIComponent(r.domain_name)}`),
                },
                {
                  label: 'Issue renewal invoice (1 yr)',
                  separated: true,
                  disabled: busyRowId === r.id,
                  onSelect: () =>
                    actOn(r.id, () => adminDomainService.renew(r.id, 1), 'Renewal invoice issued'),
                },
                // Both "Enable" and "Disable" used to be offered on every row,
                // because the row had no idea which was already true — so one of
                // the two was always a no-op you could not tell apart from the
                // real one. Now that `auto_renew` is on the view, offer the move
                // that changes something.
                {
                  label: r.auto_renew ? 'Disable auto-renew' : 'Enable auto-renew',
                  disabled: busyRowId === r.id,
                  onSelect: () =>
                    actOn(
                      r.id,
                      () => adminDomainService.toggleAutoRenew(r.id, !r.auto_renew),
                      r.auto_renew ? 'Auto-renew disabled' : 'Auto-renew enabled',
                    ),
                },
                {
                  label: 'Lock at registry',
                  separated: true,
                  disabled: busyRowId === r.id,
                  onSelect: () => actOn(r.id, () => adminDomainService.setLock(r.id, 'lock'), 'Locked'),
                },
                {
                  label: 'Unlock at registry',
                  disabled: busyRowId === r.id,
                  onSelect: () => actOn(r.id, () => adminDomainService.setLock(r.id, 'unlock'), 'Unlocked'),
                },
                {
                  label: 'Email EPP code to registrant',
                  separated: true,
                  tone: 'danger',
                  disabled: busyRowId === r.id,
                  onSelect: () =>
                    actOn(r.id, () => adminDomainService.sendEppCode(r.id), 'EPP code sent'),
                },
              ]}
            />
          </div>
        ),
        width: 'w-12',
        align: 'right',
      },
    ],
    [busyRowId, navigate],
  );

  return (
    <UniversalPage
      title="Domains"
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Domains' }]}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRefreshFromRegistrar(true)}
            disabled={refreshing}
            loading={refreshing}
          >
            {refreshing ? null : <RefreshCw className="h-4 w-4" />}
            {refreshing ? 'Refreshing…' : 'Refresh from registrar'}
          </Button>
          <Button size="sm" variant="marketing" className="text-[11px]" onClick={onImport} disabled={importing} loading={importing}>
            {importing ? null : <DownloadCloud className="h-4 w-4" />}
            {importing ? 'Importing…' : 'Import new'}
          </Button>
        </>
      }
    >
      <DomainsBalanceBanner />
      <TldMarginCard />

      <Card interactive={false} className="border-info/30 bg-info/5">
        <div className="flex gap-3 p-4 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <div className="space-y-1 text-foreground">
            <p>
              Every customer's domain is here. Click a row to open the detail view, or use the row
              <strong> ⋯ menu</strong> for renew, auto-renew, lock, and EPP actions. <strong>Refresh from
              registrar</strong> pulls fresh expiry + auto-renew state from domains.co.za in one call;
              <strong> Import new</strong> picks up domains that exist at the registrar but aren't
              linked yet.
            </p>
            {unhydratedCount > 0 && (
              <p className="text-xs text-muted-foreground">
                {unhydratedCount} of {rows.length} domain{rows.length === 1 ? '' : 's'} have not yet been
                refreshed from the registrar — click "Refresh from registrar" to populate expiry and status.
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="status" className="text-sm text-muted-foreground">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="autorenew" className="sr-only">
              Auto-renew
            </label>
            <select
              id="autorenew"
              value={autoRenewFilter}
              onChange={(e) => setAutoRenewFilter(e.target.value as AutoRenewFilter)}
              className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              title="A domain with auto-renew off will lapse on its expiry date unless someone renews it by hand"
            >
              {AUTO_RENEW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Expires"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search domain, owner, email"
            className="ml-auto w-full max-w-xs"
          />
        </div>

        {table.filters.activeCount > 0 && unhydratedCount > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            {unhydratedCount} domain{unhydratedCount === 1 ? '' : 's'} with no known
            expiry {unhydratedCount === 1 ? 'is' : 'are'} excluded while an Expires
            period is selected — choose “Not yet hydrated” to see them.
          </p>
        )}
      </Card>

      <UniversalTable<AdminDomainRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        caption={loading ? undefined : table.caption}
        onRowClick={(r) => navigate(`/admin/domains/${encodeURIComponent(r.domain_name)}`)}
        isLoading={loading}
        error={error}
        empty={{
          icon: Globe,
          title: rows.length === 0 ? 'No domains linked yet' : 'No matches',
          description:
            rows.length === 0
              ? 'Click "Import new" to pull domains from the registrar, or wait for customers to register one.'
              : 'Try a different status or search term.',
        }}
      />
    </UniversalPage>
  );
}
